import { useRef } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useParams } from "next/navigation";

import { ChatUnreadMessagesCount } from "@/components/chat/ChatUnreadMessagesCount";
import { useStore } from "@/store";
import { chatPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";

import { ListItemTitle } from "../documents/ListItemTitle";
import { useRowFocusWithin } from "../useRowFocusWithin";
import { TreeItem } from "../useSidebarNav";

import { DirectMessageUserpic, useDirectMessageLabel } from "./DirectMessagePerson";
import { useOpenDirectChat } from "./useOpenDirectChat";

import type { UserShort } from "@/api";
import type { ChatModel } from "@/models/chat/ChatModel";

const ROW_CLASS_NAME = "flex items-center gap-1.5 w-full min-h-[34px] pl-1 pr-2";

/** A person in the direct messages list: links to the chat with them, or starts one on click. */
export const DirectMessageRow = observer(function DirectMessageRow({
  user,
  chat,
}: {
  user: UserShort;
  chat?: ChatModel;
}) {
  const { view } = useStore();
  const { openDirectChat, isOpening } = useOpenDirectChat();
  const { chatId } = useParams<{ [key: string]: string | undefined }>();
  const isActive = !!chat && chatId === chat.uid;
  const unreadMessagesCount = chat?.unreadMessagesCount ?? 0;
  const hasUnreadMessages = unreadMessagesCount > 0;
  const label = useDirectMessageLabel(user);
  const rowRef = useRef<HTMLDivElement>(null);
  const { focusWithin, focusWithinProps } = useRowFocusWithin();

  const content = (
    <>
      <DirectMessageUserpic user={user} />
      <ListItemTitle
        type="chat"
        className={twMerge("py-0", hasUnreadMessages && "font-medium")}
        title={label}
      />
    </>
  );

  return (
    <li role="none">
      <TreeItem
        ref={rowRef}
        focused={focusWithin}
        {...focusWithinProps}
        aria-label={label}
        onActivate={() => rowRef.current?.querySelector<HTMLElement>("a, button")?.click()}
        className={twMerge(
          "relative flex rounded-6 hover:bg-sidebar-button-hover-inverted-bg",
          isActive && "border-blue-100 dark:border-blue-900 bg-blue-100 dark:bg-blue-900",
        )}
      >
        {chat ? (
          <Link
            href={chatPageUrl({ chatId: chat.uid })}
            tabIndex={-1}
            aria-current={isActive ? "page" : undefined}
            className={twMerge(ROW_CLASS_NAME, hasUnreadMessages && "pr-10 sm:pr-[46px]")}
            onClick={() => {
              // Same-URL link: no route change to close the phone panel
              if (isActive) {
                runInAction(() => {
                  view.isMobileDocsVisible = false;
                });
              }
            }}
          >
            {content}
          </Link>
        ) : (
          <button
            type="button"
            tabIndex={-1}
            disabled={isOpening}
            className={ROW_CLASS_NAME}
            onClick={() => void openDirectChat(user.uid)}
          >
            {content}
          </button>
        )}
        <ChatUnreadMessagesCount
          count={unreadMessagesCount}
          className="absolute top-1/2 -translate-y-1/2 right-2"
        />
      </TreeItem>
    </li>
  );
});
